import React, { useState, useEffect, useRef } from "react";
import { TextField, Button } from "@mui/material";
import styled from "@emotion/styled";
import { Navigate, useNavigate } from "react-router-dom";
import { setUser } from "../redux/session";
import { connect } from "react-redux";
import {
    signInEmailAndPassword,
    getContacts,
    getCurrentUser,
    createEmptyContact,
    deleteContact,
    getContactsListener,
    getFactsByUserListener,
    getContactsByTagId,
    getTagsByUserListener,
} from "../firebase";
import { RootState } from "../redux/root-store";
import HeaderLayout from "../components/header-layout";
import {
    ContactMaster,
    ContactDescription,
    SortingOptions,
    ContactTag,
} from "../types";
import ProfileFollowers from "../components/profile-followers";
import MySharedProfile from "../components/my-shared-profile";
import { CONTACT_MASTER } from "../default-types";
import moment from "moment";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import {
    setContacts,
    setSortingOptions,
    setAllTagsList,
    setAllFacts,
} from "../redux/contacts";
import TrialBanner from "../components/trial-banner";

const Container = styled.div`
    display: flex;
    padding: 1em 1em 0em 1em;
    width: 100%;
    height: 100%;
    position: relative;
`;

const ResponsivePanel = styled(Panel)`
    overflow: hidden !important;
    @media only screen and (max-width: 720px) {
        overflow: hidden !important;
    }
`;

const ResponsivePanelGroup = styled(PanelGroup)`
    overflow: hidden !important;
`;

const ResponsivePanelResizeHandle = styled(PanelResizeHandle)`
    @media only screen and (max-width: 720px) {
        display: none;
    }
`;

const mapDispatchToProps = {
    setUser,
    setContacts,
    setSortingOptions,
    setAllTagsList,
    setAllFacts,
};
const mapStateToProps = (state: RootState) => ({
    loggedUser: state.session.user,
    contacts: state.contacts.list,
    sortingOptions: state.contacts.sortingOptions,
    allTags: state.contacts.allTagsObject,
    allTagsList: state.contacts.allTagsList,
    allFacts: state.contacts.allFacts,
});
type dispatchType = typeof mapDispatchToProps;

interface Props extends dispatchType, ReturnType<typeof mapStateToProps> {}

const Contacts = (props: Props) => {
    const {
        loggedUser,
        setUser,
        setContacts,
        contacts,
        sortingOptions,
        setSortingOptions,
        allTags,
        allTagsList,
        setAllTagsList,
        allFacts,
        setAllFacts,
    } = props;
    const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(true);
    const [selectedContact, setSelectedContact] =
        useState<ContactMaster | null>(null);
    const [editContact, setEditContact] = useState<boolean>(false);

    const handleUpdateContact = (contact: ContactMaster | null) => {
        /*
        if (contact !== null)
            setContacts((contacts) =>
                contacts.map((oldContact: ContactMaster) =>
                    oldContact.contact_id === contact.contact_id
                        ? contact
                        : oldContact
                )
            );
         */
        setSelectedContact(contact);
    };

    const handleLogOut = () => {
        setUser(null);
    };

    const handleAddNewContact = async () => {
        if (!loggedUser) return;
        const contact = await createEmptyContact();
        setEditContact(true);
        setSelectedContact(contact);
    };

    const handleSelectContact = (contact: ContactMaster) => {
        setSelectedContact(contact);
        setEditContact(false);
    };

    const handleDeleteContact = async (contact: ContactMaster) => {
        await deleteContact(contact);
        if (
            selectedContact &&
            contact.contact_id === selectedContact.contact_id
        )
            setSelectedContact(null);
    };

    const handleAddTagToSearch = async (tag: ContactTag) => {
        if (!loggedUser) return;
        const contactIds = await getContactsByTagId(
            loggedUser.user_id,
            tag.tag_id
        );
        const contactObj = contactIds.reduce(
            (acc: { [tagId: string]: boolean }, val: string) => ({
                ...acc,
                [val]: true,
            }),
            {}
        );
        setSortingOptions((sortingOptions: SortingOptions) => ({
            ...sortingOptions,
            contactIds: contactObj,
            keyword:
                sortingOptions.keyword === tag.description
                    ? sortingOptions.keyword
                    : "",
            tagName: tag.description,
        }));
    };

    const handleSearchByHowYouMet = (
        howYouMet: string,
        howYouMetId: string
    ) => {
        setSortingOptions({
            ...sortingOptions,
            keyword: "",
            tagName: "",
            howYouMet,
            howYouMetId,
        });
    };

    useEffect(() => {
        if (allTags[sortingOptions.keyword]) {
            handleAddTagToSearch(allTags[sortingOptions.keyword]);
        }
    }, [sortingOptions.keyword]);

    useEffect(() => {
        if (!loggedUser) return;
        const unsubscribeContacts = getContactsListener(
            loggedUser.user_id,
            (contacts: ContactMaster[]) => {
                setContacts(contacts);
                setSelectedContact(
                    (selectedContact) =>
                        (selectedContact &&
                            contacts.find(
                                (c: ContactMaster) =>
                                    c.contact_id === selectedContact.contact_id
                            )) ||
                        null
                );
                setIsLoadingContacts(false);
            }
        );

        const unsubscribeFacts = getFactsByUserListener(
            loggedUser.user_id,
            (facts: ContactDescription[]) => {
                setAllFacts(
                    facts.map((fact: ContactDescription) => ({
                        ...fact,
                        lowercaseDescription: fact.description?.toLowerCase(),
                        lowercaseKidName: fact.kidName?.toLowerCase(),
                        lowercaseLocation: fact.location?.toLowerCase(),
                    }))
                );
            }
        );

        const unsubscribeTags = getTagsByUserListener(
            loggedUser.user_id,
            (tags: ContactTag[]) => {
                setAllTagsList(tags);
            }
        );
        return () => {
            unsubscribeContacts();
            unsubscribeFacts();
            unsubscribeTags();
        };
    }, []);

    const howYouMetLookUp = React.useCallback(
        (concept: string): { howYouMet: string; howYouMetId: string }[] => {
            const matches: { howYouMet: string; howYouMetId: string }[] = [];
            if (concept === "") return [];
            const lowercaseConcept = concept.toLowerCase();
            const dupsObj: { [data: string]: boolean } = {};
            let exactMatch = false;
            contacts.forEach((contact: ContactMaster) => {
                if (contact.name.toLowerCase().includes(lowercaseConcept)) {
                    matches.push({
                        howYouMet: contact.name,
                        howYouMetId: contact.contact_id,
                    });
                    if (contact.name.toLowerCase() === lowercaseConcept) {
                        exactMatch = true;
                    }
                }
                if (
                    contact.howyoumeet_id?.length === 0 &&
                    contact.howyoumeet
                        .toLowerCase()
                        .includes(lowercaseConcept) &&
                    !dupsObj[contact.howyoumeet]
                ) {
                    dupsObj[contact.howyoumeet] = true;
                    matches.push({
                        howYouMet: contact.howyoumeet,
                        howYouMetId: "",
                    });
                }
            });
            if (!exactMatch) {
                matches.push({
                    howYouMet: `Add Entity ${concept}`,
                    howYouMetId: `Add Entity ${concept}`,
                });
                matches.push({
                    howYouMet: `Add Person ${concept}`,
                    howYouMetId: `Add Person ${concept}`,
                });
            }
            return matches;
        },
        [contacts]
    );

    if (!loggedUser) return null;

    return (
        <HeaderLayout>
            <Container>
                <ResponsivePanelGroup
                    autoSaveId="contact-screen-panels"
                    direction="horizontal"
                    style={{ width: "100%" }}
                >
                    <ResponsivePanel minSize={35}>
                        <ProfileFollowers />
                    </ResponsivePanel>
                    <ResponsivePanelResizeHandle
                        style={{ zIndex: 10, width: "2px", background: "#ccc" }}
                    />
                    <ResponsivePanel minSize={45}>
                        <MySharedProfile
                            allTags={allTagsList}
                            isEditingContact={editContact}
                            key={
                                selectedContact
                                    ? selectedContact.contact_id
                                    : "undefined"
                            }
                            setContact={handleUpdateContact}
                            setIsEditingContact={setEditContact}
                            onDeleteContact={handleDeleteContact}
                            searchByTag={(tag: ContactTag) => {
                                handleAddTagToSearch(tag);
                            }}
                            searchByHowYouMet={handleSearchByHowYouMet}
                            howYouMetLookUp={howYouMetLookUp}
                            goToContact={(contactId: string) => {
                                const contact = contacts.find(
                                    (contact: ContactMaster) =>
                                        contact.contact_id === contactId
                                );
                                if (contact) {
                                    setSelectedContact(contact);
                                    setEditContact(false);
                                }
                            }}
                        />
                    </ResponsivePanel>
                </ResponsivePanelGroup>
            </Container>
            <TrialBanner />
        </HeaderLayout>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Contacts);
